import { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import {
  withButtonAlignment,
  withRtlSupport,
} from '@skyscanner/backpack-web/bpk-component-icon';
import ArrowIcon from '@skyscanner/backpack-web/bpk-component-icon/sm/long-arrow-right';
import BpkImage from '@skyscanner/backpack-web/bpk-component-image';
import { BpkButtonLink } from '@skyscanner/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';

import getIcon from '../../common/Icon';

import type { InteractiveMapItem } from '@skyscanner-internal/landing-pages-shared-types/src/types-shared/InteractiveMapProps';

import STYLES from './MapPOICard.module.scss';

const AlignedArrowIcon = withButtonAlignment(withRtlSupport(ArrowIcon));

const MAX_SNIPPET_LENGTHS = {
  DEFAULT: 180,
  NO_BUTTON: 280,
  NO_CAPTION: 220,
  NO_BUTTON_OR_CAPTION: 300,
};

const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const getSnippetLength = (caption: string, cta: InteractiveMapItem['cta']) => {
  if (!caption && !cta) {
    return MAX_SNIPPET_LENGTHS.NO_BUTTON_OR_CAPTION;
  }
  if (!caption) {
    return MAX_SNIPPET_LENGTHS.NO_CAPTION;
  }
  if (!cta) {
    return MAX_SNIPPET_LENGTHS.NO_BUTTON;
  }
  return MAX_SNIPPET_LENGTHS.DEFAULT;
};

const MapPOICard = ({
  bodyText,
  caption = '',
  cta = undefined,
  id,
  image,
  isSelected,
  readMoreLabel,
  title,
  type,
  typeIcon,
}: InteractiveMapItem & { isSelected: boolean }) => {
  const snippetBodyElement = useRef<HTMLDivElement>(null);
  const fullBodyElement = useRef<HTMLDivElement>(null);

  const MAX_SNIPPET_LENGTH = getSnippetLength(caption, cta);
  const isExpandable = bodyText.length > MAX_SNIPPET_LENGTH;
  const [isExpanded, setIsExpanded] = useState(!isExpandable);
  const [snippetHeight, setSnippetHeight] = useState<number | 'auto'>('auto');
  const [fullHeight, setFullHeight] = useState<number | 'auto'>('auto');

  const SmallIcon = getIcon(typeIcon, 'small');
  const LargeIcon = getIcon(typeIcon);

  // Set the initial heights
  useEffect(() => {
    if (snippetBodyElement.current) {
      setSnippetHeight(snippetBodyElement.current.scrollHeight);
    }
    if (fullBodyElement.current) {
      setFullHeight(fullBodyElement.current.scrollHeight);
    }
  }, [snippetBodyElement, fullBodyElement]);

  // Keep track of both the snippet and full body text elements heights
  // so we can animate between them
  useEffect(() => {
    const handleResize = debounce(() => {
      if (snippetBodyElement.current) {
        setSnippetHeight(snippetBodyElement.current.scrollHeight);
      }
      if (fullBodyElement.current) {
        setFullHeight(fullBodyElement.current.scrollHeight);
      }
    }, 100);

    const observer = new ResizeObserver(handleResize);

    if (snippetBodyElement.current) {
      observer.observe(snippetBodyElement.current);
    }
    if (fullBodyElement.current) {
      observer.observe(fullBodyElement.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [snippetBodyElement, fullBodyElement]);

  // Resets the expanded state when the card is no longer selected
  useEffect(() => {
    if (!isSelected && isExpandable && isExpanded) {
      setIsExpanded(false);
    }
  }, [isSelected, isExpanded, isExpandable]);

  return (
    <div className={STYLES.MapPOICard}>
      <div className={STYLES.Image}>
        <BpkImage
          src={image.src}
          altText={image.altText || ''}
          aspectRatio={304 / 160}
        />
      </div>

      <div className={STYLES.ContentSnippet}>
        <div className={STYLES.ContentTitles}>
          <div className={STYLES.Type}>
            <span className={classNames(STYLES.Icon, STYLES['Icon--small'])}>
              <SmallIcon />
            </span>
            <span className={classNames(STYLES.Icon, STYLES['Icon--large'])}>
              <LargeIcon />
            </span>
            <span className={STYLES.Label}>
              <BpkText tagName="p">{type}</BpkText>
            </span>
          </div>

          <div className={STYLES.Title}>
            <BpkText tagName="h3" textStyle={TEXT_STYLES.heading3}>
              {title}
            </BpkText>
          </div>
        </div>

        {caption && (
          <div className={STYLES.TagLine}>
            <BpkText textStyle={TEXT_STYLES.caption}>{caption}</BpkText>
          </div>
        )}

        <div className={STYLES.ContentBody}>
          <div
            className={classNames(
              STYLES.BodyText,
              isExpanded && STYLES['BodyText--expanded'],
            )}
            style={{
              height: isExpanded ? fullHeight : snippetHeight,
            }}
          >
            <div className={STYLES.BodyTextSnippet} ref={snippetBodyElement}>
              <BpkText textStyle={TEXT_STYLES.base}>
                {bodyText.slice(0, MAX_SNIPPET_LENGTH).trimEnd()}&hellip;{' '}
                <BpkButtonLink
                  alternate
                  aria-controls={`full-text-${id}`}
                  aria-expanded={isExpanded}
                  aria-label={`Expand to read the full content about ${title}`}
                  onClick={() => setIsExpanded(true)}
                  tabIndex={isSelected ? 0 : -1}
                >
                  {readMoreLabel}
                </BpkButtonLink>
              </BpkText>
            </div>
            <div
              id={`full-text-${id}`}
              className={STYLES.BodyTextFull}
              ref={fullBodyElement}
              aria-hidden={!isExpanded}
            >
              <BpkText textStyle={TEXT_STYLES.base}>{bodyText}</BpkText>
            </div>
          </div>

          {cta?.href && cta?.label && (
            <div className={STYLES.CTA}>
              <BpkButtonV2
                data-testid="cta-button"
                type={BUTTON_TYPES.linkOnDark}
                href={cta.href}
                tabIndex={isSelected ? 0 : -1}
              >
                {cta.label} <AlignedArrowIcon />
              </BpkButtonV2>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MapPOICard;
